/**
 * SG 풀싱크(Entity Report Sync) 변경 이력 어댑터
 *
 * SG 백엔드 RESULT_JSON_DATA 구조:
 *   { syncUpdates: [ { keyType, action: 'add'|'update'|'delete', displayName,
 *                      scrapingName, mismatchFields, before:[], after:[] } ], parsingResults: {} }
 *
 * 고객이 알 수 없는 기술 표현(GetEntityInfo, profileId, UUID, true/false 등) 을 노출하지 않도록:
 *   - 카테고리: keyType → 친화 라벨 (Company Profile / Officers & Directors / Share Capital / …)
 *   - 헤더: displayName(누구의 데이터인지) 우선
 *   - 필드명: camelCase/기술키 → 사람이 읽는 라벨, 내부 ID/UUID 필드는 숨김
 *   - 값: UUID 숨김, true/false → Yes/No
 *
 * → 공용 ReportDiffPanel 의 DiffChangeItem[] 으로 변환.
 */
import type { DiffChangeItem, DiffFieldChange, DiffChangeType } from '@shared/components/reportDiff.types';
import type { SgSyncUpdate, SgSyncResult } from '../api/entityReportSyncApi';

export interface SgSyncHistoryGroup {
    reportId: number;
    fromJobIndex: number | null;
    toJobIndex: number | null;
    comparisonDate: string | null;
    reportStatus: string | null;
    details: Array<{
        scrapingType: string | null;
        scrapingName: string | null;
        /** Shares Sync Changes Hide (L_REPORT_CLIENTS_DETAIL.IS_HIDE) — 탭 배너만 사용 */
        isHide?: boolean;
        result: SgSyncResult | null;
    }>;
}

/** GetShareCapital / GetShareholderList detail 여부 */
export function isSharesScrapingName(scrapingName: string | null | undefined): boolean {
    const s = String(scrapingName || '').toLowerCase();
    return s.includes('getsharecapital') || s.includes('getshareholderlist');
}

/** DB ENTERED_MEMBER 센티널 / BizFile 미제공 — 날짜 없음 */
function isSentinelOrEmptyDate(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    const s = String(value).trim();
    if (!s) return true;
    return /^1900-01-01/.test(s);
}

function isLcUfShareholderCategoryPair(a: unknown, b: unknown): boolean {
    const A = String(a ?? '').trim().toUpperCase();
    const B = String(b ?? '').trim().toUpperCase();
    if (!A || !B) return false;
    return (A === 'LC' && B === 'UF') || (A === 'UF' && B === 'LC');
}

function firstSyncSlice(raw: unknown): Record<string, unknown> {
    if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
        return raw[0] as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return raw as Record<string, unknown>;
    }
    return {};
}

function isNoiseMismatchField(
    field: string,
    before: Record<string, unknown>,
    after: Record<string, unknown>,
): boolean {
    const f = String(field || '').trim();
    if (f === 'effectiveDate') {
        const b = before.effectiveDate ?? before.EffectiveDate ?? '';
        const a = after.effectiveDate ?? after.EffectiveDate ?? '';
        return isSentinelOrEmptyDate(b) && isSentinelOrEmptyDate(a);
    }
    if (f === 'shareholderCategory') {
        return isLcUfShareholderCategoryPair(
            before.shareholderCategory ?? before.ShareholderCategory,
            after.shareholderCategory ?? after.ShareholderCategory,
        );
    }
    return false;
}

/**
 * update/conflict 인데 mismatch가 1900 effectiveDate · LC↔UF category 뿐이면 노이즈.
 * Sync Changes / Change History 표시에서 제외 (재 Sync 전 UI 보완).
 */
export function isNoiseOnlyShareSyncUpdate(u: SgSyncUpdate): boolean {
    const action = String(u.action || '').toLowerCase();
    if (action !== 'conflict' && action !== 'update') return false;
    const fields = Array.isArray(u.mismatchFields)
        ? u.mismatchFields.map((f) => String(f || '').trim()).filter(Boolean)
        : [];
    if (fields.length === 0) return false;
    const before = firstSyncSlice(u.before);
    const after = firstSyncSlice(u.after);
    return fields.every((f) => isNoiseMismatchField(f, before, after));
}

/** @deprecated use isNoiseOnlyShareSyncUpdate */
export function isNoiseSentinelEffectiveDateOnly(u: SgSyncUpdate): boolean {
    return isNoiseOnlyShareSyncUpdate(u);
}

/**
 * 최신 회차 Shares detail 이 모두 IS_HIDE 이면 Shares 탭 Sync Changes 블록 숨김.
 * (Hide 시 Capital+Shareholder 둘 다 1 — 한쪽만 있어도 every 로 숨김)
 * Change History 모달은 이 판정을 쓰지 않음.
 */
export function isSharesSyncChangesHidden(group: SgSyncHistoryGroup | null | undefined): boolean {
    if (!group) return false;
    const shareDetails = (group.details || []).filter((d) => isSharesScrapingName(d.scrapingName));
    if (shareDetails.length === 0) return false;
    return shareDetails.every((d) => !!d.isHide);
}

// ── 카테고리(keyType) → 친화 라벨 + Entity 상세 탭 ─────────────────────────
const KEYTYPE_INFO: Record<string, { label: string; tab?: string }> = {
    entityinfo: { label: 'Company Profile', tab: 'profile' },
    entityprofile: { label: 'Company Profile', tab: 'profile' },
    foreignentityinfo: { label: 'Foreign Entity Info', tab: 'profile' },
    positionholderinfo: { label: 'Officers & Directors', tab: 'officer' },
    userprofile: { label: 'Officer Profile', tab: 'officer' },
    contactuserprofile: { label: 'Officer Contact Address', tab: 'officer' },
    sharecapital: { label: 'Share Capital', tab: 'shares' },
    shareholderlist: { label: 'Shareholders', tab: 'shares' },
    controllerlist: { label: 'Controller', tab: 'controller' },
    nomineelist: { label: 'Nominee', tab: 'officer' },
};

function categoryInfo(u: SgSyncUpdate): { label: string; tab?: string } {
    const kt = String(u.keyType || '').toLowerCase().replace(/[\s_-]/g, '');
    if (KEYTYPE_INFO[kt]) return KEYTYPE_INFO[kt];
    // fallback: scrapingName 키워드 매칭 (GetEntityInfo / GetPositionHolderInfo 등)
    const s = String(u.scrapingName || '').toLowerCase();
    if (/position|officer|director/.test(s)) return { label: 'Officers & Directors', tab: 'officer' };
    if (/sharecapital|share.*capital/.test(s)) return { label: 'Share Capital', tab: 'shares' };
    if (/shareholder/.test(s)) return { label: 'Shareholders', tab: 'shares' };
    if (/controller/.test(s)) return { label: 'Controller', tab: 'controller' };
    if (/nominee/.test(s)) return { label: 'Nominee', tab: 'officer' };
    if (/userprofile|contactuserprofile/.test(s)) return { label: 'Officer Profile', tab: 'officer' };
    if (/entity|company/.test(s) && !/userprofile|contactuserprofile/.test(s)) {
        return { label: 'Company Profile', tab: 'profile' };
    }
    return { label: 'Change', tab: undefined };
}

function actionToType(action?: string, u?: SgSyncUpdate): DiffChangeType {
    const a = String(action || '').toLowerCase();
    if (a === 'add') return 'ADD';
    if (a === 'delete') return 'DELETE';
    // Shares: 기존 RESULT conflict → UPDATE UI (Needs review 아님)
    if (a === 'conflict') {
        const kt = String(u?.keyType || '').toLowerCase().replace(/[\s_-]/g, '');
        if (
            kt === 'sharecapital' ||
            kt === 'shareholderlist' ||
            kt === 'shareholder' ||
            isSharesScrapingName(u?.scrapingName)
        ) {
            return 'UPDATE';
        }
        return 'CONFLICT';
    }
    return 'UPDATE';
}

// ── 값/필드 humanize ──────────────────────────────────────────────────────
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function isUuid(v: unknown): boolean {
    return typeof v === 'string' && UUID_RE.test(v.trim());
}

/** 고객 Change History 에 노출하지 않는 EntityInfo 필드 (ACRA annual filing 메타) */
const CUSTOMER_HIDDEN_SYNC_FIELDS = new Set([
    'financialYearPeriod',
    'financialYearPeriodAcra',
    'lastAgmDate',
    'lastFilingDate',
    'lastFyeDate',
    'lastFinancialYearEndDate',
    'shareholderCategory',
    'ShareholderCategory',
    // Officers & Directors — 비교·고객 노출 제외 (옛 Result UI 방어)
    'officerCategory',
    'OfficerCategory',
]);

/** 내부 식별자/메타 필드는 숨김 (고객에게 무의미) */
function isHiddenField(field: string, before: unknown, after: unknown): boolean {
    if (!field || field.startsWith('_')) return true;
    if (CUSTOMER_HIDDEN_SYNC_FIELDS.has(field)) return true;
    if (/id$/i.test(field) || /_id$/i.test(field)) return true; // profileId, contactCd 류 ID 접미사
    if (isUuid(before) || isUuid(after)) return true;           // 값이 UUID → 내부 식별자
    return false;
}

/** 자주 나오는 기술 필드 → 사람이 읽는 라벨 */
const FIELD_LABELS: Record<string, string> = {
    bcpFlag: 'BCP',
    fcIncorpCountry: 'Jurisdiction',
    fcIncorpDate: 'Foreign Incorporation Date',
    financialYearPeriod: 'Financial Year Period',
    lastAgmDate: 'Last AGM Date',
    lastFilingDate: 'Last Filing Date',
    lastFyeDate: 'Last FYE Date',
    ndnsExemptionFlag: 'NDNS Exemption',
    ndnsNominatorExistFlag: 'NDNS Nominator Exists',
    appointmentStatus: 'Appointment Status',
    appointmentDate: 'Appointment Date',
    cessationDate: 'Cessation Date',
    officerCategory: 'Officer Category',
    shareType: 'Share Type',
    shareClass: 'Share Class',
    numberOfShares: 'Number of Shares',
    issuedCapital: 'Issued Capital',
    paidUpCapital: 'Paid-up Capital',
    currency: 'Currency',
    nationality: 'Nationality',
    residentialAddress: 'Residential Address',
    correspondenceAddress: 'Correspondence Address',
    identificationNumber: 'Identification Number',
    identificationType: 'Identification Type',
    foreignAddr1: 'Foreign Address Line 1',
    foreignAddr2: 'Foreign Address Line 2',
    uen: 'Registration Number',
    UEN: 'Registration Number',
    entityStatus: 'Status of Entity',
    entityType: 'Entity Type',
    registrationDate: 'Incorporation Date',
    currentFyeDate: 'Financial Year End',
    currentAgmDueDate: 'Next AGM Due Date',
    currentFilingDueDate: 'Next AR Due Date',
    primarySsicCode: 'Primary Activity',
    secondarySsicCode: 'Secondary Activity',
    streetName: 'Street Name',
    buildingName: 'Building/Estate Name',
    levelNo: 'Level',
    unitNo: 'Unit',
    registrationIdNo: 'Identification Number',
    blkhseNo: 'Block/House No',
    blkHseNo: 'Block/House No',
    contactBlkhseNo: 'Contact Block/House No',
    contactBlkHseNo: 'Contact Block/House No',
};

const ACRONYMS = new Set(['agm', 'fye', 'uen', 'nric', 'fin', 'acra', 'bcp', 'ndns', 'eci', 'ar', 'ssic', 'msic', 'id']);

function humanizeFieldLabel(field: string): string {
    if (FIELD_LABELS[field]) return FIELD_LABELS[field];
    let s = String(field || '')
        .replace(/_/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim();
    // 마지막 "Flag" 는 가독성 위해 제거 (예: "Bcp Flag" → "BCP")
    s = s.replace(/\s*flag$/i, '').trim();
    s = s.split(' ').map((w) => {
        const lw = w.toLowerCase();
        if (ACRONYMS.has(lw)) return w.toUpperCase();
        return w.charAt(0).toUpperCase() + w.slice(1);
    }).join(' ');
    return s || field;
}

function humanizeValue(v: unknown): unknown {
    if (v === null || v === undefined || v === '') return v;
    const s = String(v).trim();
    if (isUuid(s)) return '';
    if (/^true$/i.test(s)) return 'Yes';
    if (/^false$/i.test(s)) return 'No';
    return v;
}

// ── 슬라이스 값 추출 ──────────────────────────────────────────────────────
function valueForField(raw: unknown, field: string): unknown {
    const slices: Array<Record<string, unknown>> = Array.isArray(raw)
        ? (raw as Array<Record<string, unknown>>)
        : raw && typeof raw === 'object' ? [raw as Record<string, unknown>] : [];
    for (const s of slices) {
        if (s && Object.prototype.hasOwnProperty.call(s, field) && s[field] !== undefined && s[field] !== null && s[field] !== '') return s[field];
    }
    for (const s of slices) {
        if (s && Object.prototype.hasOwnProperty.call(s, field)) return s[field];
    }
    return undefined;
}

function unionFieldKeys(raw: unknown): string[] {
    const slices: Array<Record<string, unknown>> = Array.isArray(raw)
        ? (raw as Array<Record<string, unknown>>)
        : raw && typeof raw === 'object' ? [raw as Record<string, unknown>] : [];
    const keys = new Set<string>();
    for (const s of slices) {
        if (s && typeof s === 'object') for (const k of Object.keys(s)) keys.add(k);
    }
    return Array.from(keys);
}

/** 헤더용 이름 추출 (displayName 없을 때 슬라이스에서) */
function pickName(u: SgSyncUpdate): string {
    const nameKeys = ['personName', 'name', 'fullName', 'companyName', 'officerName', 'shareholderName', 'controllerName'];
    for (const f of nameKeys) {
        const v = valueForField(u.after, f) ?? valueForField(u.before, f);
        if (v != null && String(v).trim()) return String(v).trim();
    }
    return '';
}

/** before/after + mismatchFields → DiffFieldChange[] (숨김필드 제외, 라벨/값 humanize) */
function buildChanges(u: SgSyncUpdate): DiffFieldChange[] {
    let fields = Array.isArray(u.mismatchFields) ? u.mismatchFields.slice() : [];
    if (fields.length === 0) {
        fields = Array.from(new Set<string>([...unionFieldKeys(u.before), ...unionFieldKeys(u.after)]));
    }
    const before = firstSyncSlice(u.before);
    const after = firstSyncSlice(u.after);
    return fields
        .filter((field) => !isNoiseMismatchField(field, before, after))
        .map((field) => ({ field, b: valueForField(u.before, field), a: valueForField(u.after, field) }))
        .filter(({ field, b, a }) => !isHiddenField(field, b, a))
        .map(({ field, b, a }) => ({
            field: humanizeFieldLabel(field),
            before: humanizeValue(b),
            after: humanizeValue(a),
        }));
}

/** ADD/DELETE 표시용 — 슬라이스 → 라벨:값 (숨김필드 제외, humanize) */
function sliceToFields(raw: unknown): Record<string, unknown> {
    const slices: Array<Record<string, unknown>> = Array.isArray(raw)
        ? (raw as Array<Record<string, unknown>>)
        : raw && typeof raw === 'object' ? [raw as Record<string, unknown>] : [];
    const out: Record<string, unknown> = {};
    for (const s of slices) {
        if (!s || typeof s !== 'object') continue;
        for (const [k, v] of Object.entries(s)) {
            if (isHiddenField(k, v, v)) continue;
            const label = humanizeFieldLabel(k);
            if (!(label in out)) out[label] = humanizeValue(v);
        }
    }
    return out;
}

/** ShareCapital displayName 에 같은 회차 ShareholderList 주주 이름 연결 */
function parsePersonClassDisplayName(displayName: string): { personName: string; classLabel: string } {
    const s = String(displayName || '').trim();
    const idx = s.indexOf(' — ');
    if (idx < 0) return { personName: '', classLabel: s };
    return { personName: s.slice(0, idx).trim(), classLabel: s.slice(idx + 3).trim() };
}

function enrichShareCapitalUpdatesInGroup(updates: SgSyncUpdate[]): SgSyncUpdate[] {
    const holdersByClass = new Map<string, Set<string>>();
    for (const u of updates) {
        if (String(u.keyType || '') !== 'ShareholderList') continue;
        const { personName, classLabel } = parsePersonClassDisplayName(String(u.displayName || ''));
        if (!personName || !classLabel) continue;
        if (!holdersByClass.has(classLabel)) holdersByClass.set(classLabel, new Set());
        holdersByClass.get(classLabel)!.add(personName);
    }
    if (!holdersByClass.size) return updates;

    return updates.map((u) => {
        if (String(u.keyType || '') !== 'ShareCapital') return u;
        const classLabel = String(u.displayName || '').trim();
        if (!classLabel || classLabel.includes(' — ')) return u;
        const names = holdersByClass.get(classLabel);
        if (!names?.size) return u;
        const nameList = [...names].sort((a, b) => a.localeCompare(b)).join(', ');
        return { ...u, displayName: `${nameList} — ${classLabel}` };
    });
}

function toItem(u: SgSyncUpdate): DiffChangeItem {
    const type = actionToType(u.action, u);
    const { label, tab } = categoryInfo(u);
    const who = (u.displayName && String(u.displayName).trim()) || pickName(u) || '';
    const kt = String(u.keyType || '').toLowerCase().replace(/[\s_-]/g, '');
    const isShareCapital = kt === 'sharecapital';
    const shareCapitalHasHolder = isShareCapital && who.includes(' — ');
    const item: DiffChangeItem = {
        changeType: type,
        identifier: who || label,
        subtitle: who
            ? (shareCapitalHasHolder ? 'Shareholder' : (isShareCapital ? 'Company share class' : label))
            : undefined,
        actionable: !!tab,
        actionTab: tab,
        personId: u.contactCd || u.profileId || u.userProfileId || undefined,
    };
    if (type === 'ADD') item.fields = sliceToFields(u.after);
    else if (type === 'DELETE') item.fields = sliceToFields(u.before);
    else item.changes = buildChanges(u);
    return item;
}

/** PositionHolderInfo ADD/DELETE — LC_SH-only (or strip 후 직책 없음)는 Officer Sync Changes/팝업에서 제외 */
function firstResultSlice(raw: unknown): Record<string, unknown> {
    if (Array.isArray(raw) && raw[0] && typeof raw[0] === 'object') {
        return raw[0] as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
    return {};
}

function nonLcShPositionCodes(held: unknown): string[] {
    if (!Array.isArray(held)) return [];
    return held
        .map((ph) => {
            if (!ph || typeof ph !== 'object') return '';
            const row = ph as Record<string, unknown>;
            const code = String(
                row.positionHeld ?? row.PositionHeld ?? row.positionHeldName ?? row.PositionHeldName ?? ''
            ).trim();
            return code && code !== 'LC_SH' ? code : '';
        })
        .filter(Boolean);
}

function isPositionHolderKeyType(u: SgSyncUpdate): boolean {
    const kt = String(u.keyType || '').toLowerCase().replace(/[\s_-]/g, '');
    if (kt === 'positionholderinfo') return true;
    const sn = String(u.scrapingName || '').toLowerCase();
    return sn.includes('getpositionholderinfo') || sn.includes('positionholder');
}

/** 옛 Result UI 방어: LC_SH-only ADD/DELETE 는 주주이지 Officers 비교 대상이 아님 */
function shouldSkipLcShOnlyOfficer(u: SgSyncUpdate): boolean {
    if (!isPositionHolderKeyType(u)) return false;
    const action = String(u.action || '').toLowerCase();
    if (action !== 'add' && action !== 'delete') return false;

    const slice = firstResultSlice(action === 'delete' ? u.before : u.after);
    const held = slice.positionsHeld ?? slice.PositionsHeld;
    // strip 후 Result 슬라이스에 positionsHeld 가 없으면 LC_SH-only 로 간주
    if (held === undefined) return true;
    if (!Array.isArray(held) || held.length === 0) return true;
    return nonLcShPositionCodes(held).length === 0;
}

function isShareCapitalUpdate(u: SgSyncUpdate): boolean {
    const kt = String(u.keyType || '').toLowerCase().replace(/[\s_-]/g, '');
    if (kt === 'sharecapital') return true;
    return String(u.scrapingName || '').toLowerCase().includes('getsharecapital');
}

function isShareholderListUpdate(u: SgSyncUpdate): boolean {
    const kt = String(u.keyType || '').toLowerCase().replace(/[\s_-]/g, '');
    if (kt === 'shareholderlist' || kt === 'shareholder') return true;
    return String(u.scrapingName || '').toLowerCase().includes('getshareholderlist');
}

/**
 * 같은 회차에 ShareholderList 변경이 있으면 ShareCapital syncUpdates 는 UI에서 숨김
 * (Shares Sync Changes / Change History 중복 CONFLICT 방지).
 * ShareholderList 가 비어 있을 때만 Capital 표시.
 */
function shouldOmitShareCapitalWhenShareholderPresent(allUpdates: SgSyncUpdate[]): boolean {
    return allUpdates.some(
        (u) =>
            isShareholderListUpdate(u) &&
            !isNoiseOnlyShareSyncUpdate(u) &&
            !shouldSkipLcShOnlyOfficer(u),
    );
}

export interface BuildGroupChangeItemsOptions {
    /**
     * true: Shares detail(IS_HIDE) syncUpdates 제외 — Entity Shares 탭 Sync Changes 전용.
     * Change History 모달은 옵션 없이 호출 (숨김 회차도 표시).
     */
    omitHiddenShareDetails?: boolean;
}

/** 회차 그룹의 모든 category(detail) syncUpdates 를 평탄화하여 DiffChangeItem[] */
export function buildGroupChangeItems(
    group: SgSyncHistoryGroup,
    options?: BuildGroupChangeItemsOptions,
): DiffChangeItem[] {
    const omitHiddenShareDetails = !!options?.omitHiddenShareDetails;
    const items: DiffChangeItem[] = [];
    const allUpdates: SgSyncUpdate[] = [];
    for (const d of group.details || []) {
        for (const u of d.result?.syncUpdates || []) allUpdates.push(u);
    }
    const omitShareCapital = shouldOmitShareCapitalWhenShareholderPresent(allUpdates);
    const enrichedUpdates = enrichShareCapitalUpdatesInGroup(allUpdates);
    let cursor = 0;
    for (const d of group.details || []) {
        const raw = d.result?.syncUpdates || [];
        const slice = enrichedUpdates.slice(cursor, cursor + raw.length);
        cursor += raw.length;
        if (
            omitHiddenShareDetails &&
            d.isHide &&
            isSharesScrapingName(d.scrapingName)
        ) {
            continue;
        }
        for (const u of slice) {
            if (shouldSkipLcShOnlyOfficer(u)) continue;
            if (isNoiseOnlyShareSyncUpdate(u)) continue;
            if (omitShareCapital && isShareCapitalUpdate(u)) continue;
            const item = toItem(u);
            // UPDATE/CONFLICT 인데 (숨김 제거 후) 변경 필드가 없으면 의미 없는 항목 → 스킵
            if (
                (item.changeType === 'UPDATE' || item.changeType === 'CONFLICT') &&
                (!item.changes || item.changes.length === 0)
            ) {
                continue;
            }
            // ADD/DELETE 인데 표시할 필드가 하나도 없으면 스킵
            if ((item.changeType === 'ADD' || item.changeType === 'DELETE') && (!item.fields || Object.keys(item.fields).length === 0)) continue;
            items.push(item);
        }
    }
    return items;
}

/** 회차 라벨 (예: Report#166 → #167) */
export function groupLabel(group: SgSyncHistoryGroup): string {
    const from = group.fromJobIndex;
    const to = group.toJobIndex;
    if (from != null && to != null) return `Report#${from} → #${to}`;
    if (to != null) return `Report#${to}`;
    return `Report #${group.reportId}`;
}

// ── 상세 화면 필드 단위 인디케이터용 ─────────────────────────────
/** 한 필드의 싱크 변경 정보 */
export interface SyncFieldChange {
    type: 'UPDATE' | 'CONFLICT';
    before: unknown;
    after: unknown;
    note?: string;
}

/** SG sync 필드명 → Contact 폼 lookup 키 (BasicIndividualSection syncChange) */
const SG_FORM_FIELD_KEYS: Record<string, string> = {
    idType: 'identification_type',
    idtype: 'identification_type',
    uin: 'identification_number',
    registrationIdNo: 'identification_number',
    registrationidno: 'identification_number',
    birthDate: 'date_of_birth',
    birthdate: 'date_of_birth',
    dateOfBirth: 'date_of_birth',
    nationality: 'nationality',
    gender: 'gender',
    race: 'race',
    email: 'email',
    mobileNo: 'mobile_no',
    mobileno: 'mobile_no',
    mobileNoCountryCode: 'mobile_no_country_code',
    positionsHeld: 'positions_held',
    positionsheld: 'positions_held',
    name: 'person_name',
    personName: 'person_name',
    foreignAddr1: 'foreignAddressLine1',
    foreignaddr1: 'foreignAddressLine1',
    foreignAddr2: 'foreignAddressLine2',
    foreignaddr2: 'foreignAddressLine2',
    postalCode: 'postalCode',
    entryDate: 'dateOfEntry',
    identification_type: 'identification_type',
    identification_number: 'identification_number',
    identificationNo: 'identification_number',
    date_of_birth: 'date_of_birth',
    person_name: 'person_name',
};

/** SG EntityInfo / EntityProfile raw 필드 → Profile 탭 폼 키 */
const ENTITY_PROFILE_FORM_FIELD_KEYS: Record<string, string> = {
    uen: 'registrationNumber',
    entitystatus: 'status',
    entitytype: 'entityType',
    fcincorpcountry: 'jurisdiction',
    fcincorpdate: 'incorporationDate',
    registrationdate: 'incorporationDate',
    postalcode: 'postalCode',
    blkhseno: 'blockNo',
    streetname: 'streetName',
    buildingname: 'buildingName',
    levelno: 'level',
    unitno: 'unit',
    primaryssiccode: 'primaryActivityCode',
    secondaryssiccode: 'secondaryActivityCode',
    currentfyedate: 'financialYearEnd',
    currentagmduedate: 'nextAgmDueDate',
    currentfilingduedate: 'nextArDueDate',
    lastagmdate: 'lastAgmDate',
    lastardate: 'lastArDate',
    lastfinancialyearenddate: 'lastFyeDate',
    lastfilingdate: 'lastFilingDate',
    lastfyedate: 'lastFyeDate',
    financialyearperiod: 'financialYearPeriod',
};

const ENTITY_PROFILE_FIELD_ALIASES: Record<string, string[]> = {
    registrationNumber: ['uen', 'UEN'],
    status: ['entityStatus'],
    entityType: ['entityTypeCode'],
    jurisdiction: ['fcIncorpCountry', 'jurisdictionCode'],
    incorporationDate: ['registrationDate', 'fcIncorpDate'],
    financialYearPeriod: ['financialYearPeriodAcra'],
    lastAgmDate: ['lastAgmDate'],
    lastFilingDate: ['lastFilingDate'],
    lastFyeDate: ['lastFinancialYearEndDate'],
    primaryActivity: ['primarySsicCode', 'primaryActivityCode'],
    secondaryActivity: ['secondarySsicCode', 'secondaryActivityCode'],
    blockNo: ['blkhseNo', 'blkHseNo', 'blockHouseNo'],
};

/** canonical form key → UI 필드명 alias (Controller/Contact 상세) */
const FORM_FIELD_EXTRA_ALIASES: Record<string, string[]> = {
    identification_number: ['identificationNo', 'registrationIdNo'],
    nationality: ['nationalityCd'],
    date_of_birth: ['dateOfBirth'],
    foreignAddressLine1: ['foreignAddr1', 'addressLine1'],
    foreignAddressLine2: ['foreignAddr2', 'addressLine2'],
    postalCode: ['postalCode'],
    dateOfEntry: ['entryDate'],
    buildingName: ['buildingEstateName'],
    blockNo: ['blockHouseNo'],
    positions_held: ['positionsHeld'],
};

function normIdNumber(v: unknown): string {
    return String(v ?? '').replace(/[\s-]/g, '').toUpperCase();
}

function pickProfileId(u: SgSyncUpdate): string {
    return String(
        u.profileId ?? u.userProfileId ?? valueForField(u.after, 'profileId') ?? valueForField(u.before, 'profileId') ?? '',
    ).trim();
}

function matchesSgUpdate(
    u: SgSyncUpdate,
    match: { identificationNumber?: string; contactCd?: string; profileId?: string; personName?: string },
): boolean {
    const idn = normIdNumber(match.identificationNumber);
    const ccd = String(match.contactCd ?? '').trim();
    const pid = String(match.profileId ?? '').trim();
    const personName = String(match.personName ?? '').trim().toLowerCase();

    const uCcd = String(u.contactCd ?? valueForField(u.after, 'contactCd') ?? valueForField(u.before, 'contactCd') ?? '').trim();
    const uPid = pickProfileId(u);
    const uin = normIdNumber(valueForField(u.after, 'uin') ?? valueForField(u.before, 'uin'));
    const regNo = normIdNumber(
        valueForField(u.after, 'registrationIdNo') ?? valueForField(u.before, 'registrationIdNo'),
    );

    if (ccd && uCcd && ccd.toLowerCase() === uCcd.toLowerCase()) return true;
    if (pid && uPid && pid.toLowerCase() === uPid.toLowerCase()) return true;
    if (idn && uin && idn === uin) return true;
    if (idn && regNo && idn === regNo) return true;

    if (personName) {
        const display = String(u.displayName || '').trim().toLowerCase();
        const rowName = String(
            valueForField(u.after, 'name') ?? valueForField(u.before, 'name') ?? '',
        ).trim().toLowerCase();
        const head = display.split(' — ')[0]?.trim() || display;
        if (head && (head.includes(personName) || personName.includes(head))) return true;
        if (rowName && (rowName.includes(personName) || personName.includes(rowName))) return true;
    }
    return false;
}

function buildRawFieldChanges(u: SgSyncUpdate): Array<{ field: string; before: unknown; after: unknown }> {
    let fields = Array.isArray(u.mismatchFields) ? u.mismatchFields.slice() : [];
    if (fields.length === 0) {
        fields = Array.from(new Set<string>([...unionFieldKeys(u.before), ...unionFieldKeys(u.after)]));
    }
    return fields
        .map((field) => ({ field, before: valueForField(u.before, field), after: valueForField(u.after, field) }))
        .filter(({ field, before, after }) => !isHiddenField(field, before, after));
}

function setEntityProfileFieldChange(map: Map<string, SyncFieldChange>, rawField: string, change: SyncFieldChange): void {
    const raw = String(rawField || '').trim();
    if (!raw) return;
    map.set(raw, change);
    const formKey = ENTITY_PROFILE_FORM_FIELD_KEYS[raw.toLowerCase()];
    if (formKey) {
        map.set(formKey, change);
        for (const alias of ENTITY_PROFILE_FIELD_ALIASES[formKey] || []) {
            map.set(alias, change);
        }
    }
    applyDisplayLabelToMap(map, humanizeFieldLabel(raw), change);
}

function isProfileSyncUpdate(u: SgSyncUpdate): boolean {
    return categoryInfo(u).tab === 'profile';
}

function setFormFieldChange(map: Map<string, SyncFieldChange>, rawField: string, change: SyncFieldChange): void {
    const raw = String(rawField || '').trim();
    if (!raw) return;
    map.set(raw, change);
    const formKey = SG_FORM_FIELD_KEYS[raw] ?? SG_FORM_FIELD_KEYS[raw.toLowerCase()];
    if (formKey) {
        map.set(formKey, change);
        for (const alias of FORM_FIELD_EXTRA_ALIASES[formKey] || []) {
            map.set(alias, change);
        }
    }
    applyDisplayLabelToMap(map, humanizeFieldLabel(raw), change);
}

/** ReportDiffPanel humanized 라벨 → 상세 폼 필드 키 */
function applyDisplayLabelToMap(map: Map<string, SyncFieldChange>, label: string, change: SyncFieldChange): void {
    const raw = String(label || '').trim();
    if (!raw) return;
    map.set(raw, change);
    const lower = raw.toLowerCase();
    const rules: Array<{ test: (s: string) => boolean; keys: string[] }> = [
        { test: (s) => s === 'nationality', keys: ['nationality', 'nationalityCd'] },
        { test: (s) => s.includes('identification') || s.includes('registration id'), keys: ['identificationNo', 'identification_number', 'registrationIdNo'] },
        { test: (s) => (s.includes('foreign') && s.includes('1')) || s.includes('foreign address line 1'), keys: ['foreignAddressLine1', 'foreignAddr1'] },
        { test: (s) => (s.includes('foreign') && s.includes('2')) || s.includes('foreign address line 2'), keys: ['foreignAddressLine2', 'foreignAddr2'] },
        { test: (s) => s.includes('postal'), keys: ['postalCode'] },
        { test: (s) => s.includes('birth'), keys: ['dateOfBirth', 'date_of_birth', 'birthDate'] },
        { test: (s) => s.includes('email'), keys: ['email', 'contactEmail'] },
        { test: (s) => s.includes('mobile'), keys: ['mobileNo', 'mobile_no', 'mobile_no_country_code'] },
        { test: (s) => s.includes('position') && (s.includes('held') || s.includes('appointment')), keys: ['positionsHeld', 'positions_held'] },
        { test: (s) => s.includes('registration number') || s === 'uen', keys: ['registrationNumber', 'uen'] },
        { test: (s) => s.includes('status of entity') || (s.includes('entity') && s.includes('status')), keys: ['status', 'entityStatus'] },
        { test: (s) => s.includes('entity type'), keys: ['entityType'] },
        { test: (s) => s.includes('jurisdiction') || s.includes('incorporation country'), keys: ['jurisdiction', 'fcIncorpCountry'] },
        { test: (s) => s.includes('foreign incorporation date'), keys: ['incorporationDate', 'fcIncorpDate', 'registrationDate'] },
        { test: (s) => s.includes('incorporation date') || s === 'registration date', keys: ['incorporationDate', 'registrationDate', 'fcIncorpDate'] },
        { test: (s) => s.includes('financial year period'), keys: ['financialYearPeriod', 'financialYearPeriodAcra'] },
        { test: (s) => s.includes('last agm'), keys: ['lastAgmDate'] },
        { test: (s) => s.includes('last filing'), keys: ['lastFilingDate'] },
        { test: (s) => s.includes('last fye') || s.includes('last financial year end'), keys: ['lastFyeDate', 'lastFinancialYearEndDate'] },
        { test: (s) => s.includes('financial year end') || s.includes('current fye'), keys: ['financialYearEnd', 'currentFyeDate'] },
        { test: (s) => s.includes('next agm') || s.includes('agm due'), keys: ['nextAgmDueDate', 'currentAgmDueDate'] },
        { test: (s) => s.includes('next ar') || s.includes('filing due'), keys: ['nextArDueDate', 'currentFilingDueDate'] },
        { test: (s) => s.includes('next eci') || s.includes('eci due'), keys: ['nextEciDueDate'] },
        { test: (s) => s.includes('primary activity') || s.includes('primary ssic'), keys: ['primaryActivity', 'primaryActivityCode', 'primarySsicCode'] },
        { test: (s) => s.includes('secondary activity') || s.includes('secondary ssic'), keys: ['secondaryActivity', 'secondaryActivityCode', 'secondarySsicCode'] },
        { test: (s) => s.includes('block') || s.includes('house no'), keys: ['blockNo', 'blkhseNo', 'blockHouseNo'] },
        { test: (s) => s.includes('street'), keys: ['streetName'] },
        { test: (s) => s.includes('building') || s.includes('estate'), keys: ['buildingName', 'buildingEstateName'] },
        { test: (s) => (s === 'level' || s.includes('level no')), keys: ['level', 'levelNo'] },
        { test: (s) => s === 'unit' || s.includes('unit no'), keys: ['unit', 'unitNo'] },
    ];
    for (const rule of rules) {
        if (rule.test(lower)) {
            for (const k of rule.keys) map.set(k, change);
        }
    }
}

function normalizePersonName(v: unknown): string {
    return String(v ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function hasMatchKeys(match: { identificationNumber?: string; contactCd?: string; profileId?: string; personName?: string }): boolean {
    return !!(
        normIdNumber(match.identificationNumber)
        || String(match.contactCd ?? '').trim()
        || String(match.profileId ?? '').trim()
        || normalizePersonName(match.personName)
    );
}

/** 배너(ReportDiffPanel) 항목이 현재 상세 대상 사람인지 */
function matchPersonToDiffItem(
    item: DiffChangeItem,
    match: { identificationNumber?: string; contactCd?: string; profileId?: string; personName?: string },
): boolean {
    const idn = normIdNumber(match.identificationNumber);
    const ccd = normContactCd(match.contactCd);
    const pid = normContactCd(match.profileId);
    const person = normalizePersonName(match.personName);
    const itemPerson = normContactCd(item.personId);

    if (ccd && itemPerson && ccd === itemPerson) return true;
    if (pid && itemPerson && pid === itemPerson) return true;

    const ident = normalizePersonName(item.identifier);
    if (person && ident) {
        const head = ident.split(' — ')[0]?.trim() || ident;
        if (head === person || ident.includes(person) || person.includes(head)) return true;
    }

    if (idn) {
        for (const ch of item.changes || []) {
            const lab = String(ch.field).toLowerCase();
            if (lab.includes('identification') || lab.includes('registration') || lab === 'uin') {
                const b = normIdNumber(ch.before);
                const a = normIdNumber(ch.after);
                if (b === idn || a === idn) return true;
            }
        }
        if (ident && normIdNumber(ident) === idn) return true;
    }
    return false;
}

function applyDiffItemToMap(map: Map<string, SyncFieldChange>, item: DiffChangeItem): void {
    const type: SyncFieldChange['type'] = item.changeType === 'CONFLICT' ? 'CONFLICT' : 'UPDATE';
    for (const ch of item.changes || []) {
        applyDisplayLabelToMap(map, ch.field, { type, before: ch.before, after: ch.after });
    }
    if (item.fields) {
        for (const [label, val] of Object.entries(item.fields)) {
            const fc: SyncFieldChange =
                item.changeType === 'DELETE'
                    ? { type, before: val, after: undefined }
                    : { type, before: undefined, after: val };
            applyDisplayLabelToMap(map, label, fc);
        }
    }
}

/**
 * 특정 사람(contactCd / profileId / uin)의 SG 싱크 변경을 필드별 맵으로.
 * 최신 회차(groups[0])만 반영.
 */
export function buildFieldChangeMap(
    groups: SgSyncHistoryGroup[],
    match: { identificationNumber?: string; contactCd?: string; profileId?: string; personName?: string },
): Map<string, SyncFieldChange> {
    const map = new Map<string, SyncFieldChange>();
    if (!hasMatchKeys(match)) return map;

    const g = groups[0];
    if (!g) return map;

    // 1) 배너/모달과 동일한 DiffChangeItem 파이프라인 (humanized 라벨)
    for (const item of buildGroupChangeItems(g)) {
        if (!matchPersonToDiffItem(item, match)) continue;
        if (item.changeType === 'UPDATE' && (!item.changes || item.changes.length === 0)) continue;
        applyDiffItemToMap(map, item);
    }

    // 2) raw syncUpdate 보조 (camelCase 키)
    for (const d of g.details || []) {
        for (const u of d.result?.syncUpdates || []) {
            if (!matchesSgUpdate(u, match)) continue;
            for (const c of buildRawFieldChanges(u)) {
                setFormFieldChange(map, c.field, {
                    type: 'UPDATE',
                    before: c.before,
                    after: c.after,
                });
            }
        }
    }
    return map;
}

function normContactCd(v: unknown): string {
    return String(v ?? '').trim().toLowerCase();
}

function itemHasRealChange(item: DiffChangeItem): boolean {
    if (item.changeType === 'ADD' || item.changeType === 'DELETE') return true;
    const ch = item.changes || [];
    if (!ch.length) return false;
    return ch.some((c) => {
        const b = String(c.before ?? '').trim();
        const a = String(c.after ?? '').trim();
        return b !== a;
    });
}

/** 최신 회차에서 특정 탭의 변경 항목만 */
export function extractLatestTabChangeItems(groups: SgSyncHistoryGroup[], tab: string): DiffChangeItem[] {
    const g = groups[0];
    if (!g) return [];
    return buildGroupChangeItems(g)
        .filter((i) => i.actionTab === tab)
        .filter((i) => itemHasRealChange(i));
}

/** Officer 탭 — 행 하이라이트용 contactCd / 식별번호 집합 */
export function buildOfficerSyncHighlightKeys(groups: SgSyncHistoryGroup[]): {
    contactCds: Set<string>;
    idNumbers: Set<string>;
} {
    const contactCds = new Set<string>();
    const idNumbers = new Set<string>();
    const g = groups[0];
    if (!g) return { contactCds, idNumbers };

    for (const item of extractLatestTabChangeItems(groups, 'officer')) {
        const pid = normContactCd(item.personId);
        if (pid) contactCds.add(pid);
        for (const ch of item.changes || []) {
            const label = String(ch.field || '').toLowerCase();
            if (label.includes('identification') || label === 'uin') {
                const b = normIdNumber(ch.before);
                const a = normIdNumber(ch.after);
                if (b) idNumbers.add(b);
                if (a) idNumbers.add(a);
            }
        }
    }

    for (const d of g.details || []) {
        for (const u of d.result?.syncUpdates || []) {
            const { tab } = categoryInfo(u);
            if (tab !== 'officer') continue;
            const ccd = normContactCd(u.contactCd);
            if (ccd) contactCds.add(ccd);
            const pid = normContactCd(u.profileId ?? u.userProfileId);
            if (pid) contactCds.add(pid);
            const uin = normIdNumber(valueForField(u.after, 'uin') ?? valueForField(u.before, 'uin'));
            if (uin) idNumbers.add(uin);
        }
    }

    return { contactCds, idNumbers };
}

/**
 * Profile 탭(회사 정보) — 최신 회차 EntityInfo/EntityProfile 변경을 필드별 맵으로.
 * 사람 매칭 없이 profile 탭 syncUpdates 전체 반영.
 */
export function buildEntityProfileFieldChangeMap(groups: SgSyncHistoryGroup[]): Map<string, SyncFieldChange> {
    const map = new Map<string, SyncFieldChange>();
    const g = groups[0];
    if (!g) return map;

    for (const item of buildGroupChangeItems(g)) {
        if (item.actionTab !== 'profile') continue;
        if (item.changeType === 'UPDATE' && (!item.changes || item.changes.length === 0)) continue;
        applyDiffItemToMap(map, item);
    }

    for (const d of g.details || []) {
        for (const u of d.result?.syncUpdates || []) {
            if (!isProfileSyncUpdate(u)) continue;
            for (const c of buildRawFieldChanges(u)) {
                setEntityProfileFieldChange(map, c.field, {
                    type: 'UPDATE',
                    before: c.before,
                    after: c.after,
                });
            }
        }
    }
    return map;
}

/** Officer 행 1명에 해당하는 최신 sync 변경 항목 */
export function getOfficerSyncItemsForRow(
    groups: SgSyncHistoryGroup[],
    row: { contactCd?: string; idNumber?: string; profileId?: string },
): DiffChangeItem[] {
    const ccd = normContactCd(row.contactCd);
    const idn = normIdNumber(row.idNumber);
    const pid = normContactCd(row.profileId);
    return extractLatestTabChangeItems(groups, 'officer').filter((item) => {
        const person = normContactCd(item.personId);
        if (ccd && person && ccd === person) return true;
        if (pid && person && pid === person) return true;
        if (idn) {
            for (const ch of item.changes || []) {
                const label = String(ch.field || '').toLowerCase();
                if (!label.includes('identification') && label !== 'uin') continue;
                const b = normIdNumber(ch.before);
                const a = normIdNumber(ch.after);
                if (b === idn || a === idn) return true;
            }
        }
        const who = String(item.identifier || '').toLowerCase();
        if (idn && who.includes(idn.toLowerCase())) return true;
        return false;
    });
}
